import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { Cards } from './Cards';
import { InsightsTable } from './InsightsTable';
import DateRange from 'views/DriverBehavior/components/DateRange';
import MlInsightsHandler from 'handlers/ml-insights.handler';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { setDateRange, clearInsights } from 'store/ml-insights.slice';
import { useLocation } from 'react-router-dom';
import type { InsightsTableNavigationState } from 'types/ml-insights.types';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const MlInsights = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const locationState = location.state as
    | { tableState?: InsightsTableNavigationState }
    | undefined;
  const initialTableState = locationState?.tableState;
  const [restoredState, setRestoredState] = useState<
    InsightsTableNavigationState | undefined
  >(locationState?.tableState);
  const mlInsightsHandler = new MlInsightsHandler();
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme: boolean = isDarkTheme(theme);
  const [searchTerm, setSearchTerm] = useState(
    initialTableState?.filters.searchTerm ?? ''
  );
  const [sortBy, setSortBy] = useState(
    initialTableState?.filters.sortBy ?? 'Insight Start'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialTableState?.filters.sortOrder ?? 'desc'
  );
  const [selectedView, setSelectedView] = useState(
    initialTableState?.filters.selectedView ?? 'Active'
  );
  const [selectedType, setSelectedType] = useState(
    initialTableState?.filters.selectedType ?? 'All'
  );
  const [selectedIssues, setSelectedIssues] = useState<string[]>(
    restoredState?.filters.selectedIssues ?? []
  );
  const [resetSignal, setResetSignal] = useState(
    restoredState?.filters.resetSignal ?? 0
  );

  // Get date range from Redux state
  const { dateRange } = useAppSelector((state) => state.mlInsightsReducer);
  const [startDate, setStartDate] = useState<Date | null>(
    dateRange.startDate ? new Date(dateRange.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    dateRange.endDate ? new Date(dateRange.endDate) : null
  );
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(
    dateRange.selectedRange
  );
  useEffect(() => {
    setRestoredState(locationState?.tableState ?? undefined);
  }, [locationState?.tableState]);

  useEffect(() => {
    if (restoredState) {
      const { filters } = restoredState;
      setSearchTerm(filters.searchTerm);
      setSortBy(filters.sortBy);
      setSortOrder(filters.sortOrder);
      setSelectedView(filters.selectedView ?? 'Active');
      setSelectedType(filters.selectedType);
      setSelectedIssues(filters.selectedIssues);
      setResetSignal(filters.resetSignal);
    }
  }, [restoredState]);

  useEffect(() => {
    const fetchMlInsights = async () => {
      setIsLoading(true);
      dispatch(clearInsights());

      try {
        const startDateString = startDate?.toISOString().split('T')[0] ?? '';
        const endDateString = endDate?.toISOString().split('T')[0] ?? '';
        const range = selectedDateRange;

        await mlInsightsHandler.getMlInsights(
          range,
          startDateString,
          endDateString
        );
        await mlInsightsHandler.getUpdatedInsights();
      } catch (error) {
        console.error('Error fetching ML insights:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchMlInsights();
  }, [selectedDateRange, startDate, endDate]);

  const handleDateChange = (range: [Date | null, Date | null]) => {
    const [newStart, newEnd] = range;
    const startChanged =
      (newStart?.getTime() ?? null) !== (startDate?.getTime() ?? null);
    const endChanged =
      (newEnd?.getTime() ?? null) !== (endDate?.getTime() ?? null);
    if (startChanged || endChanged) {
      setResetSignal((prev: number) => prev + 1);
      setRestoredState(undefined);
    }
    setStartDate(newStart);
    setEndDate(newEnd);

    dispatch(
      setDateRange({
        selectedRange: selectedDateRange,
        startDate: range[0]?.toISOString().split('T')[0] ?? '',
        endDate: range[1]?.toISOString().split('T')[0] ?? '',
      })
    );
  };

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        color: palette.textPrimary,
        borderRadius: '5px',
        marginTop: '10px',
        padding: '10px',
      }}
    >
      <DateRange
        enableDarkTheme={enableDarkTheme}
        isSelected={selectedDateRange}
        setIsSelected={(range: string) => {
          setResetSignal((prev: number) =>
            range === selectedDateRange ? prev : prev + 1
          );
          setSelectedDateRange(range);
          setRestoredState(undefined);
          // Update Redux state when range changes
          dispatch(
            setDateRange({
              selectedRange: range,
              startDate: startDate?.toISOString().split('T')[0] ?? '',
              endDate: endDate?.toISOString().split('T')[0] ?? '',
            })
          );
        }}
        handleDateChange={handleDateChange}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        yesterday={yesterday}
        isLoading={isLoading}
        isMaxEndDate={false}
      />

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              marginTop: '15%',
            }}
          >
            <SingleFadeLoader isFullScreen={false} />
          </Box>
        </Box>
      ) : (
        <>
          <Cards enableDarkTheme={enableDarkTheme} />
          <InsightsTable
            enableDarkTheme={enableDarkTheme}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedIssues={selectedIssues}
            setSelectedIssues={setSelectedIssues}
            restoredState={restoredState}
            resetSignal={resetSignal}
          />
        </>
      )}
    </Box>
  );
};

export default MlInsights;
